import XIcon from '@duyank/icons/regular/X';
import React, { forwardRef, ForwardRefRenderFunction } from 'react';
import Slider from '../../common/slider/Slider';
import { useEditor, useSelectedLayers } from '../../hooks';
import { ImageLayerProps } from '../../layers';
import Sidebar, { SidebarProps } from './Sidebar';

type ImageManipulationSidebarProps = SidebarProps;
const ImageManipulationSidebar: ForwardRefRenderFunction<
  HTMLDivElement,
  ImageManipulationSidebarProps
> = ({ ...props }, ref) => {
  const { selectedLayers } = useSelectedLayers();
  const { actions, activePage } = useEditor((state) => ({
    activePage: state.activePage,
  }));
  const imageProps = selectedLayers[0].data.props as ImageLayerProps;
  const updateImageAttribute = (
    field:
      | 'brightness'
      | 'contrast'
      | 'grayscale'
      | 'saturation'
      | 'hueRotate'
      | 'blur',
    value: number
  ) => {
    actions.history
      .throttle(2000)
      .setProp<ImageLayerProps>(activePage, selectedLayers[0].id, {
        image: {
          [field]: value || null,
        },
      });
  };
  const reset = () => {
    actions.setProp<ImageLayerProps>(activePage, selectedLayers[0].id, {
      image: {
        url: imageProps.image.original ?? imageProps.image.url,
        thumb: imageProps.image.original ?? imageProps.image.thumb,
        original: null,
        brightness: null,
        contrast: null,
        saturation: null,
        hueRotate: null,
        grayscale: null,
        blur: null,
      },
    });
  };

  const isEdited =
    imageProps.image.brightness ||
    imageProps.image.contrast ||
    imageProps.image.saturation ||
    imageProps.image.hueRotate ||
    imageProps.image.grayscale ||
    imageProps.image.blur;

  return (
    <Sidebar ref={ref} {...props}>
      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 48,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          padding: '0 20px',
        })}
      >
        <p
          css={(theme) => ({
            lineHeight: '48px',
            fontWeight: 600,
            color: theme.colors.content,
            flexGrow: 1,
          })}
        >
          Edit Image
        </p>
        <div
          css={(theme) => ({
            fontSize: 20,
            flexShrink: 0,
            width: 32,
            height: 32,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
          onClick={() => {
            actions.setSidebar();
          }}
        >
          <XIcon />
        </div>
      </div>
      <div
        css={(theme) => ({
          padding: '0 20px',
          display: 'grid',
          paddingBottom: 70,
          maxHeight: 'calc(100vh - 71px)',
          overflow: 'auto',
        })}
      >
        <div
          css={(theme) => ({
            width: '100%',
            paddingBottom: '100%',
            position: 'relative',
            marginTop: 20,
          })}
        >
          <div
            css={(theme) => ({
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#EBECF0',
              'html.dark &': {
                background: theme.colors.backgroundItem,
              }
            })}
          >
            <img
              alt={imageProps.image.url}
              css={{
                maxWidth: '100%',
                maxHeight: '100%',
                filter: `
                  ${
                    imageProps.image.brightness
                      ? `brightness(${imageProps.image.brightness + 100}%)`
                      : ''
                  }
                  ${
                    imageProps.image.contrast
                      ? `contrast(${imageProps.image.contrast + 100}%)`
                      : ''
                  }
                  ${
                    imageProps.image.saturation
                      ? `saturate(${imageProps.image.saturation + 100}%)`
                      : ''
                  }
                  ${
                    imageProps.image.grayscale
                      ? `grayscale(${imageProps.image.grayscale}%)`
                      : ''
                  }
                  ${
                    imageProps.image.hueRotate
                      ? `hue-rotate(${imageProps.image.hueRotate}deg)`
                      : ''
                  }
                  ${
                    imageProps.image.blur
                      ? `blur(${imageProps.image.blur}px)`
                      : ''
                  }
              `.trim(),
              }}
              src={imageProps.image.original ?? imageProps.image.url}
            />
          </div>
        </div>
        <div
          css={(theme) => ({
            display: 'flex',
            justifyContent: 'end',
          })}
        >
          <span
            css={(theme) => ({
              cursor: isEdited ? 'pointer' : undefined,
              opacity: isEdited ? 1 : 0.5,
              color: theme.colors.primary,
              marginTop: 8,
            })}
            onClick={reset}
          >
            Reset
          </span>
        </div>
        <div css={(theme) => ({ display: 'grid', rowGap: 24 })}>
          <div
            css={(theme) => ({
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            })}
          >
            <div>Brightness</div>
            <Slider
              hideInput
              max={100}
              min={-100}
              value={imageProps.image.brightness || 0}
              onChange={(v) => updateImageAttribute('brightness', v)}
            />
          </div>
          <div
            css={(theme) => ({
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            })}
          >
            <div>Contrast</div>
            <Slider
              hideInput
              max={100}
              min={-100}
              value={imageProps.image.contrast || 0}
              onChange={(v) => updateImageAttribute('contrast', v)}
            />
          </div>
          <div
            css={(theme) => ({
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            })}
          >
            <div>Saturation</div>
            <Slider
              hideInput
              max={100}
              min={-100}
              value={imageProps.image.saturation || 0}
              onChange={(v) => updateImageAttribute('saturation', v)}
            />
          </div>
          <div
            css={(theme) => ({
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            })}
          >
            <div>Hue Rotate</div>
            <Slider
              hideInput
              max={180}
              min={0}
              value={imageProps.image.hueRotate || 0}
              onChange={(v) => updateImageAttribute('hueRotate', v)}
            />
          </div>
          <div
            css={(theme) => ({
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            })}
          >
            <div>Grayscale</div>
            <Slider
              hideInput
              max={100}
              min={0}
              value={imageProps.image.grayscale || 0}
              onChange={(v) => updateImageAttribute('grayscale', v)}
            />
          </div>
          <div
            css={(theme) => ({
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            })}
          >
            <div>Blur</div>
            <Slider
              hideInput
              max={30}
              min={0}
              value={imageProps.image.blur || 0}
              onChange={(v) => updateImageAttribute('blur', v)}
            />
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default forwardRef<HTMLDivElement, ImageManipulationSidebarProps>(
  ImageManipulationSidebar
);
